'use client';

import { FONT_TEXT } from '@/app/fonts';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { ShareableChart } from '@/components/blog/ShareableChart';

const data = [
  { time: '< 1 min', rate: 391, display: '391%', color: '#10b981', label: 'Best' },
  { time: '< 5 min', rate: 100, display: '100%', color: '#ff34a7', label: 'Baseline' },
  { time: '30 min', rate: 62, display: '62%', color: '#294bfd', label: '' },
  { time: '1 hour', rate: 36, display: '36%', color: '#94a3b8', label: '' },
  { time: '24 hours', rate: 17, display: '17%', color: '#94a3b8', label: '' },
  { time: '48+ hours', rate: 5, display: '5%', color: '#dc2626', label: 'Dead' },
];

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ payload: typeof data[0] }>;
}

function CustomTooltip({ active, payload }: CustomTooltipProps) {
  if (!active || !payload?.length) return null;
  const item = payload[0].payload;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-4 py-3">
      <p className="font-semibold text-sm text-gray-900">Response in {item.time}</p>
      <p className="text-sm text-gray-600">
        Contact rate: <span className="font-medium">{item.display}</span> vs 5-min baseline
      </p>
    </div>
  );
}

export function SpeedToLeadChart() {
  return (
    <ShareableChart title="Speed to Lead Impact on Conversion">
      <h3 className="text-lg font-bold text-gray-900 mb-1" style={{ fontFamily: FONT_TEXT }}>
        Speed-to-Lead: Every Minute Costs You Money
      </h3>
      <p className="text-sm text-gray-500 mb-4" style={{ fontFamily: FONT_TEXT }}>
        Contact rates indexed to 5-minute response baseline (100%)
      </p>

      <div className="w-full" style={{ height: '320px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 20, right: 30, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
            <XAxis 
              dataKey="time" 
              tick={{ fontSize: 12, fill: '#374151', fontFamily: FONT_TEXT }}
              axisLine={{ stroke: '#e5e7eb' }}
            />
            <YAxis 
              tick={{ fontSize: 11, fill: '#6b7280', fontFamily: FONT_TEXT }}
              tickFormatter={(v: number) => `${v}%`}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f9fafb' }} />
            <Bar dataKey="rate" radius={[4, 4, 0, 0]} barSize={48}>
              {data.map((entry, index) => (
                <Cell key={index} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-3 p-3 rounded-lg border-l-4" style={{ borderColor: '#ff34a7', backgroundColor: '#fff5fb' }}>
        <p className="text-sm text-gray-700" style={{ fontFamily: FONT_TEXT }}>
          <strong>The 21x stat:</strong> Leads contacted within 5 minutes are 21x more likely to convert than those contacted after 30 minutes. After 24 hours, the lead is essentially dead. Speed isn't a nice-to-have — it's the entire game.
        </p>
      </div>

      <p className="text-xs text-gray-400 mt-3" style={{ fontFamily: FONT_TEXT }}>
        Source: leadpops.com · Based on Lead Response Management Study, InsideSales.com research
      </p>
    </ShareableChart>
  );
}
